import { supabase } from '../lib/supabase';
import { AdoptionInquiry } from '../types/models';
import { Database } from '../types/database.types';
import { notificationService } from './notificationService';

type InquiryRow = Database['public']['Tables']['adoption_inquiries']['Row'];
type InquiryInsert = Database['public']['Tables']['adoption_inquiries']['Insert'];
type InquiryUpdate = Database['public']['Tables']['adoption_inquiries']['Update'];

// Add new interfaces for extended fields
interface AdoptionInquiryUpdate {
  status?: 'pending' | 'reviewing' | 'approved' | 'rejected' | 'completed';
  adminReviewedAt?: string;
  adminReviewedBy?: string;
  rejectionReason?: string;
  interviewScheduledAt?: string;
  interviewWhatsappLink?: string;
  interviewNotes?: string;
  paymentStatus?: string;
  paymentAmount?: number;
  paymentCompletedAt?: string;
  applicationStep?: number;
  submittedAt?: string;
  shelterNotes?: string;
  responseMessage?: string;
}

// Helper functions for case conversion
function toSnakeCase(obj: any): any {
  if (obj === null || obj === undefined) return obj;
  if (typeof obj !== 'object') return obj;
  if (Array.isArray(obj)) return obj.map(toSnakeCase);

  const snakeCaseObj: any = {};
  for (const key in obj) {
    if (obj.hasOwnProperty(key)) {
      const snakeKey = key.replace(/[A-Z]/g, letter => `_${letter.toLowerCase()}`);
      snakeCaseObj[snakeKey] = toSnakeCase(obj[key]);
    }
  }
  return snakeCaseObj;
}

function toCamelCase(obj: any): any {
  if (obj === null || obj === undefined) return obj;
  if (typeof obj !== 'object') return obj;
  if (Array.isArray(obj)) return obj.map(toCamelCase);

  const camelCaseObj: any = {};
  for (const key in obj) {
    if (obj.hasOwnProperty(key)) {
      const camelKey = key.replace(/_([a-z])/g, (_, letter) => letter.toUpperCase());
      camelCaseObj[camelKey] = toCamelCase(obj[key]);
    }
  }
  return camelCaseObj;
}

export const adoptionInquiryService = {
  async create(inquiry: Omit<AdoptionInquiry, 'id' | 'createdAt' | 'updatedAt' | 'status' | 'shelterNotes' | 'responseMessage'>): Promise<AdoptionInquiry> {
    const insertData: InquiryInsert = {
      pet_id: inquiry.petId,
      user_id: inquiry.userId,
      contact_name: inquiry.contactName,
      contact_email: inquiry.contactEmail,
      contact_phone: inquiry.contactPhone,
      preferred_contact: inquiry.preferredContact,
      pet_questions: inquiry.petQuestions,
      concerns: inquiry.concerns,
      special_interests: inquiry.specialInterests,
      housing_type: inquiry.housingType,
      has_yard: inquiry.hasYard,
      has_other_pets: inquiry.hasOtherPets,
      other_pets_details: inquiry.otherPetsDetails,
      has_children: inquiry.hasChildren,
      children_ages: inquiry.childrenAges,
      experience_level: inquiry.experienceLevel,
      adoption_timeline: inquiry.adoptionTimeline,
      employment_status: inquiry.employmentStatus,
      employer_name: inquiry.employerName,
      occupation: inquiry.occupation,
      work_schedule: inquiry.workSchedule,
      vet_name: inquiry.vetName,
      vet_phone: inquiry.vetPhone,
      vet_address: inquiry.vetAddress,
      landlord_name: inquiry.landlordName,
      landlord_phone: inquiry.landlordPhone,
      landlord_email: inquiry.landlordEmail,
      pet_policy_approved: inquiry.petPolicyApproved,
      whatsapp_number: inquiry.whatsappNumber,
    };

    const { data, error } = await supabase
      .from('adoption_inquiries')
      .insert(insertData)
      .select()
      .single();


    if (error) throw error;

    // Notification is now handled by a Database Trigger (on_inquiry_created)
    // keeping the code clean and secure by not querying admins on the client.

    return this.convertToCamelCase(data);
  },

  async getByUserId(userId: string): Promise<AdoptionInquiry[]> {
    const { data, error } = await supabase
      .from('adoption_inquiries')
      .select(`
        *,
        pet:pets(id, name, category, breed, age_years, age_months, image_url, shelter_location)
      `)
      .eq('user_id', userId)
      .order('created_at', { ascending: false });

    if (error) throw error;
    return (data || []).map(this.convertToCamelCase);
  },

  async getByPetId(petId: string): Promise<AdoptionInquiry[]> {
    const { data, error } = await supabase
      .from('adoption_inquiries')
      .select('*')
      .eq('pet_id', petId)
      .order('created_at', { ascending: false });

    if (error) throw error;
    return (data || []).map(this.convertToCamelCase);
  },

  async getById(inquiryId: string): Promise<AdoptionInquiry> {
    const { data, error } = await supabase
      .from('adoption_inquiries')
      .select(`
        *,
        pet:pets(id, name, category, breed, age_years, age_months, image_url, shelter_location)
      `)
      .eq('id', inquiryId)
      .single();

    if (error) throw error;
    return this.convertToCamelCase(data);
  },

  async getAllApplications(): Promise<AdoptionInquiry[]> {
    const { data, error } = await supabase
      .from('adoption_inquiries')
      .select(`
        *,
        pet:pets(id, name, category, breed, age_years, age_months, image_url, shelter_location),
        user:user_profiles!user_id(id, full_name, email),
        reviewer:user_profiles!admin_reviewed_by(id, full_name)
      `)
      .order('created_at', { ascending: false });

    if (error) throw error;
    return (data || []).map(this.convertToCamelCase);
  },

  async update(inquiryId: string, updates: Partial<Omit<AdoptionInquiry, 'id' | 'createdAt' | 'updatedAt'>>): Promise<AdoptionInquiry> {
    const updateData: InquiryUpdate = {};

    if (updates.contactName !== undefined) updateData.contact_name = updates.contactName;
    if (updates.contactEmail !== undefined) updateData.contact_email = updates.contactEmail;
    if (updates.contactPhone !== undefined) updateData.contact_phone = updates.contactPhone;
    if (updates.preferredContact !== undefined) updateData.preferred_contact = updates.preferredContact;
    if (updates.petQuestions !== undefined) updateData.pet_questions = updates.petQuestions;
    if (updates.concerns !== undefined) updateData.concerns = updates.concerns;
    if (updates.specialInterests !== undefined) updateData.special_interests = updates.specialInterests;
    if (updates.housingType !== undefined) updateData.housing_type = updates.housingType;
    if (updates.hasYard !== undefined) updateData.has_yard = updates.hasYard;
    if (updates.hasOtherPets !== undefined) updateData.has_other_pets = updates.hasOtherPets;
    if (updates.otherPetsDetails !== undefined) updateData.other_pets_details = updates.otherPetsDetails;
    if (updates.hasChildren !== undefined) updateData.has_children = updates.hasChildren;
    if (updates.childrenAges !== undefined) updateData.children_ages = updates.childrenAges;
    if (updates.experienceLevel !== undefined) updateData.experience_level = updates.experienceLevel;
    if (updates.adoptionTimeline !== undefined) updateData.adoption_timeline = updates.adoptionTimeline;
    if (updates.status !== undefined) updateData.status = updates.status;
    if (updates.shelterNotes !== undefined) updateData.shelter_notes = updates.shelterNotes;
    if (updates.responseMessage !== undefined) updateData.response_message = updates.responseMessage;
    if (updates.employmentStatus !== undefined) updateData.employment_status = updates.employmentStatus;
    if (updates.employerName !== undefined) updateData.employer_name = updates.employerName;
    if (updates.occupation !== undefined) updateData.occupation = updates.occupation;
    if (updates.workSchedule !== undefined) updateData.work_schedule = updates.workSchedule;
    if (updates.vetName !== undefined) updateData.vet_name = updates.vetName;
    if (updates.vetPhone !== undefined) updateData.vet_phone = updates.vetPhone;
    if (updates.vetAddress !== undefined) updateData.vet_address = updates.vetAddress;
    if (updates.landlordName !== undefined) updateData.landlord_name = updates.landlordName;
    if (updates.landlordPhone !== undefined) updateData.landlord_phone = updates.landlordPhone;
    if (updates.landlordEmail !== undefined) updateData.landlord_email = updates.landlordEmail;
    if (updates.petPolicyApproved !== undefined) updateData.pet_policy_approved = updates.petPolicyApproved;
    if (updates.whatsappNumber !== undefined) updateData.whatsapp_number = updates.whatsappNumber;
    if (updates.interviewScheduledAt !== undefined) updateData.interview_scheduled_at = updates.interviewScheduledAt;
    if (updates.interviewNotes !== undefined) updateData.interview_notes = updates.interviewNotes;
    if (updates.adminReviewedBy !== undefined) updateData.admin_reviewed_by = updates.adminReviewedBy;
    if (updates.adminReviewedAt !== undefined) updateData.admin_reviewed_at = updates.adminReviewedAt;
    if (updates.rejectionReason !== undefined) updateData.rejection_reason = updates.rejectionReason;

    const { data, error } = await supabase
      .from('adoption_inquiries')
      .update(updateData)
      .eq('id', inquiryId)
      .select()
      .single();

    if (error) throw error;
    return this.convertToCamelCase(data);
  },

  async updateInquiryStatus(
    inquiryId: string,
    updates: AdoptionInquiryUpdate
  ): Promise<{ data: AdoptionInquiry | null; error: string | null }> {
    try {
      const snakeCaseUpdates = toSnakeCase(updates);

      const { data, error } = await supabase
        .from('adoption_inquiries')
        .update(snakeCaseUpdates)
        .eq('id', inquiryId)
        .select()
        .single();

      if (error) {
        return { data: null, error: error.message };
      }

      return { data: toCamelCase(data) as AdoptionInquiry, error: null };
    } catch (error) {
      return {
        data: null,
        error: error instanceof Error ? error.message : 'Failed to update inquiry status'
      };
    }
  },

  async getInquiriesByStatus(status: string): Promise<{ data: AdoptionInquiry[] | null; error: string | null }> {
    try {
      const { data, error } = await supabase
        .from('adoption_inquiries')
        .select(`
          *,
          pet:pets(*),
          user:user_profiles!user_id(*)
        `)
        .eq('status', status)
        .order('created_at', { ascending: false });

      if (error) {
        return { data: null, error: error.message };
      }

      return { data: toCamelCase(data) as AdoptionInquiry[], error: null };
    } catch (error) {
      return {
        data: null,
        error: error instanceof Error ? error.message : 'Failed to fetch inquiries'
      };
    }
  },

  async getInquiryWithDocuments(inquiryId: string): Promise<{
    data: { inquiry: AdoptionInquiry; documents: any[] } | null;
    error: string | null
  }> {
    try {
      const { data: inquiryData, error: inquiryError } = await supabase
        .from('adoption_inquiries')
        .select(`
          *,
          pet:pets(*),
          user:user_profiles!user_id(*)
        `)
        .eq('id', inquiryId)
        .single();

      if (inquiryError) {
        return { data: null, error: inquiryError.message };
      }

      const { data: docsData, error: docsError } = await supabase
        .from('application_documents')
        .select('*')
        .eq('inquiry_id', inquiryId)
        .order('created_at', { ascending: false });

      if (docsError) {
        return { data: null, error: docsError.message };
      }

      return {
        data: {
          inquiry: toCamelCase(inquiryData) as AdoptionInquiry,
          documents: toCamelCase(docsData)
        },
        error: null
      };
    } catch (error) {
      return {
        data: null,
        error: error instanceof Error ? error.message : 'Failed to fetch inquiry with documents'
      };
    }
  },

  async delete(inquiryId: string): Promise<void> {
    const { error } = await supabase
      .from('adoption_inquiries')
      .delete()
      .eq('id', inquiryId);

    if (error) throw error;
  },

  convertToCamelCase(row: InquiryRow): AdoptionInquiry {
    // Use the toCamelCase helper for consistent conversion
    const converted = toCamelCase(row) as AdoptionInquiry;
    return converted;
  },
};